/**
 * @fileoverview The operation-run lifecycle — the status of a long platform operation the console follows (tenant
 * creation, a backup, a blog sync), its machine and its badge tones, declared once (TENANT-CREATION-JOB-001, owner
 * rulings Q-TCJ-3 "Fix all in this workstream" and Q-TCJ-4 "Long work is always a job").
 *
 * ## Why it lives here
 *
 * The run's status is written by the server (`@puffin-core/operation-runs`, each owning app's run table and its
 * CHECK) and read by the console (`useOperationRun`, `<OperationRunProgress>`). The console must know which statuses
 * are still moving — it polls until a terminal one — and how to draw each, so both read this one machine; the console
 * never imports the server package.
 *
 * ## The states
 *
 * - `queued` — accepted (`202 { runId }`) and waiting for a worker.
 * - `running` — an attempt is in flight; its steps, phase and percent move.
 * - `retrying` — an attempt failed on a TRANSIENT code (connection / timeout / lock), was compensated, and the next
 *   attempt is waiting. Visible, so an operator sees the failure that caused it.
 * - `succeeded` — done. Terminal.
 * - `failed` — the last attempt failed (a refusal is never retried; a transient failure exhausted its attempts), or
 *   the run was interrupted by a deploy. An operator's Retry re-queues it.
 *
 * Every status is a KEY, a terminal one mapping to `[]` — the `SHIPMENT_STATUS_TRANSITIONS` convention.
 *
 * @module @puffin-core/status-taxonomy/operationRunStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * The operation-run lifecycle, in lifecycle order.
 *
 * @type {Readonly<Object<string, string>>}
 */
const OPERATION_RUN_STATUS = Object.freeze({
  QUEUED: 'queued',
  RUNNING: 'running',
  RETRYING: 'retrying',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
});

/**
 * Every status, in lifecycle order — what a run table's status CHECK admits.
 *
 * @type {ReadonlyArray<string>}
 */
const OPERATION_RUN_STATUS_VALUES = Object.freeze(Object.values(OPERATION_RUN_STATUS));

/**
 * The statuses of a run still in flight — what a run table's partial unique index admits once per subject, and what
 * the console keeps polling.
 *
 * @type {ReadonlyArray<string>}
 */
const OPERATION_RUN_ACTIVE_STATUSES = Object.freeze([
  OPERATION_RUN_STATUS.QUEUED,
  OPERATION_RUN_STATUS.RUNNING,
  OPERATION_RUN_STATUS.RETRYING,
]);

/**
 * Every legal transition, spelled once.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const OPERATION_RUN_STATUS_TRANSITIONS = Object.freeze({
  [OPERATION_RUN_STATUS.QUEUED]: Object.freeze([
    OPERATION_RUN_STATUS.RUNNING,
    OPERATION_RUN_STATUS.FAILED,
  ]),
  [OPERATION_RUN_STATUS.RUNNING]: Object.freeze([
    OPERATION_RUN_STATUS.SUCCEEDED,
    OPERATION_RUN_STATUS.RETRYING,
    OPERATION_RUN_STATUS.FAILED,
  ]),
  [OPERATION_RUN_STATUS.RETRYING]: Object.freeze([
    OPERATION_RUN_STATUS.RUNNING,
    OPERATION_RUN_STATUS.FAILED,
  ]),
  [OPERATION_RUN_STATUS.SUCCEEDED]: Object.freeze([]),
  [OPERATION_RUN_STATUS.FAILED]: Object.freeze([OPERATION_RUN_STATUS.QUEUED]),
});

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a run at `status` may move to next.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as `constructor`) answers
 * `[]` rather than a function.
 *
 * @param {string} status - The run's current status.
 * @returns {ReadonlyArray<string>} The legal targets; `[]` when terminal or unknown.
 */
function operationRunStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(OPERATION_RUN_STATUS_TRANSITIONS, status)
    ? OPERATION_RUN_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Whether a run at `status` is still in flight (the console keeps following it).
 *
 * @param {string} status - The run's status.
 * @returns {boolean}
 */
function isOperationRunActive(status) {
  return OPERATION_RUN_ACTIVE_STATUSES.includes(status);
}

/**
 * Each status's badge TONE (`<StatusBadgeCell statusTones>`): waiting (`neutral`), moving (`info`), a failure being
 * retried (`warning`), done (`success`), failed (`danger`).
 *
 * @type {Readonly<Object<string, string>>}
 */
const OPERATION_RUN_STATUS_BADGE_TONES = Object.freeze({
  [OPERATION_RUN_STATUS.QUEUED]: BADGE_TONE.NEUTRAL,
  [OPERATION_RUN_STATUS.RUNNING]: BADGE_TONE.INFO,
  [OPERATION_RUN_STATUS.RETRYING]: BADGE_TONE.WARNING,
  [OPERATION_RUN_STATUS.SUCCEEDED]: BADGE_TONE.SUCCESS,
  [OPERATION_RUN_STATUS.FAILED]: BADGE_TONE.DANGER,
});

module.exports = {
  OPERATION_RUN_STATUS,
  OPERATION_RUN_STATUS_VALUES,
  OPERATION_RUN_ACTIVE_STATUSES,
  OPERATION_RUN_STATUS_TRANSITIONS,
  OPERATION_RUN_STATUS_BADGE_TONES,
  operationRunStatusTargets,
  isOperationRunActive,
};
